// hh-345 — авто-подбор иконки для вещей / комнат / оборудования.
//
// Бэкенд хранит поле `icon` (имя из ICON_NAMES) или null. null = иконку
// подбирает фронт автоматически по русскому названию (и категории — для
// оборудования). Этот модуль реализует подбор и единый хелпер отрисовки
// `entityIcon`, который сначала смотрит на ручной выбор, а затем падает в
// авто-подбор.
//
// Подбор намеренно простой: таблица «русское ключевое слово → имя иконки»,
// поиск подстроки в нормализованном (lowercase) названии. Этого достаточно
// для бытовых сценариев; точность можно поднять позже без смены контракта.

import { ICON_NAMES, type IconName } from './icons';

/** Тип сущности — задаёт фолбэк-иконку, если ничего не подобралось. */
export type IconEntityKind = 'item' | 'room' | 'equipment' | 'property';

/** Фолбэк-иконка по типу сущности. */
const FALLBACK_BY_KIND: Record<IconEntityKind, IconName> = {
  item: 'item',
  room: 'folder',
  equipment: 'boiler',
  property: 'home',
};

// Множество допустимых имён — для валидации ручного значения `icon`.
const ICON_NAME_SET: ReadonlySet<string> = new Set<string>(ICON_NAMES);

/** True, если строка — валидное имя иконки из реестра. */
export function isIconName(value: string | null | undefined): value is IconName {
  return value != null && ICON_NAME_SET.has(value);
}

// ─────────────────────────────────────────────────────────
// Таблицы ключевых слов → иконка.
// Порядок внутри массива важен: более специфичные слова идут раньше, но
// поиск всё равно перебирает все пары и берёт первое совпадение по списку.
// ─────────────────────────────────────────────────────────

type KeywordRule = readonly [keywords: readonly string[], icon: IconName];

// Помещения / зоны.
const ROOM_RULES: readonly KeywordRule[] = [
  [['кухн', 'кухонн'], 'kitchen'],
  [['детск'], 'kidsRoom'],
  [['спальн', 'спальня'], 'bedroom'],
  [['ванн', 'санузел', 'санузла', 'душев'], 'bathroom'],
  [['туалет', 'уборная'], 'toilet'],
  [['гостин', 'зал'], 'livingRoom'],
  [['прихож', 'коридор'], 'hallway'],
  [['кладов', 'кладовк'], 'pantry'],
  [['гараж'], 'garageRoom'],
  [['балкон', 'лоджи'], 'balcony'],
  [['кабинет', 'офис', 'рабочая комната'], 'study'],
  [['чердак', 'мансард'], 'attic'],
  [['подвал', 'погреб', 'цоколь'], 'basement'],
  [['гардероб', 'гардеробн'], 'closet'],
  [['постиро', 'прачеч'], 'laundry'],
  [['сад', 'огород', 'теплиц', 'участок', 'двор', 'террас', 'веранд'], 'garden'],
];

// Оборудование (бытовая техника / климат / водоподготовка).
const EQUIPMENT_RULES: readonly KeywordRule[] = [
  [['котёл', 'котел', 'бойлер', 'водонагрев'], 'boiler'],
  [['фильтр', 'водоподготов', 'умягчит', 'осмос'], 'filter'],
  [['кондиционер', 'кондей', 'сплит'], 'ac'],
  [['радиатор', 'батаре', 'отоплен', 'конвектор'], 'radiator'],
  [['холодильник', 'морозильн'], 'fridge'],
  [['стиральн', 'стиралк'], 'washer'],
  [['посудомо'], 'dishwasher'],
  [['микроволнов', 'свч', 'микроволновк'], 'microwave'],
  [['духов', 'духовк'], 'oven'],
  [['плита', 'варочн'], 'stove'],
  [['вытяжк', 'вытяжн'], 'hood'],
  [['чайник'], 'kettle'],
  [['пылесос'], 'vacuum'],
  [['вентилятор', 'вентиляц'], 'fan'],
  [['телевизор', 'тв ', 'теле'], 'tv'],
  [['насос'], 'drop'],
];

// Вещи (инструмент / мебель / транспорт / разное).
const ITEM_RULES: readonly KeywordRule[] = [
  // Инструмент.
  [['дрель', 'шуруповёрт', 'шуруповерт', 'перфоратор'], 'drill'],
  [['молоток', 'киянк'], 'hammer'],
  [['отвёртк', 'отвертк'], 'screwdriver'],
  [['ключ гаечн', 'гаечн', 'разводн'], 'wrench'],
  [['пила', 'пилк', 'ножовк', 'лобзик'], 'saw'],
  [['стремянк', 'лестниц'], 'ladder'],
  [['ящик с инструмент', 'инструмент'], 'toolbox'],
  // Мебель.
  [['диван', 'софа', 'кушетк'], 'sofa'],
  [['кровать', 'кроват'], 'bed'],
  [['стул', 'табурет'], 'chair'],
  [['стол', 'столик'], 'table'],
  [['полка', 'стеллаж'], 'shelf'],
  [['шкаф', 'комод'], 'wardrobe'],
  [['письменный стол'], 'desk'],
  [['зеркал'], 'mirror'],
  [['лампа', 'светильник', 'торшер'], 'lamp'],
  // Транспорт.
  [['велосипед', 'велик'], 'bike'],
  [['самокат', 'скутер'], 'scooter'],
  [['колесо', 'шина', 'покрышк', 'резина'], 'wheel'],
  [['машина', 'автомобил', 'авто '], 'car'],
  // Разное / бытовое.
  [['палатк', 'тент'], 'shield'],
  [['книг', 'учебник'], 'book'],
  [['одежд', 'куртк', 'пальто', 'футболк', 'штаны', 'брюки'], 'clothes'],
  [['обувь', 'ботинк', 'кроссовк', 'туфли', 'сапог'], 'shoes'],
  [['растен', 'цветок', 'цветы', 'горшок'], 'plant'],
  [['документ', 'паспорт', 'договор', 'справк'], 'document'],
  [['ключ', 'ключи'], 'key'],
  [['ноутбук', 'лэптоп', 'компьютер'], 'laptop'],
  [['телефон', 'смартфон'], 'phone'],
  [['часы'], 'clock'],
  [['сумк', 'рюкзак', 'чемодан', 'портфел'], 'bag'],
  [['гитар', 'скрипк', 'укулеле'], 'guitar'],
  [['мангал', 'гриль', 'барбекю'], 'grill'],
  [['аптечк', 'медикамент', 'лекарств'], 'medkit'],
  [['фотоаппарат', 'камера'], 'camera'],
  [['лыжи', 'сноуборд', 'санки'], 'snow'],
  [['коробк', 'ящик', 'контейнер'], 'box'],
];

// Собственность — подбор по названию (aidepo-2): понятие type как выбор
// пользователя убрано, иконка следует за именем объекта.
const PROPERTY_RULES: readonly KeywordRule[] = [
  [['квартир', 'апартамент'], 'apartment'],
  [['дач'], 'dacha'],
  [['гараж', 'гск', 'бокс'], 'garageBuilding'],
  [['машин', 'автомобил', 'авто', 'тачк'], 'car'],
  [['дом', 'коттедж', 'таунхаус', 'изб'], 'home'],
];

// Маппинг category оборудования (water_filter / boiler / aircon / heating /
// other) на иконку — используется до анализа названия.
const EQUIPMENT_CATEGORY_ICON: Record<string, IconName> = {
  water_filter: 'filter',
  filter: 'filter',
  boiler: 'boiler',
  aircon: 'ac',
  ac: 'ac',
  heating: 'radiator',
  // aidepo-19: общий `appliance` НЕ маппим — иначе стиральная машина и т. п.
  // получают иконку холодильника. Для appliance подбор идёт по названию.
};

/** Ищет первое правило, чьё ключевое слово содержится в нормализованном тексте. */
function matchRules(text: string, rules: readonly KeywordRule[]): IconName | null {
  for (const [keywords, icon] of rules) {
    for (const kw of keywords) {
      if (text.includes(kw)) return icon;
    }
  }
  return null;
}

/** Нормализует название: lowercase + замена «ё» на «е» для устойчивого поиска. */
function normalize(name: string): string {
  return name.toLowerCase().replace(/ё/g, 'е').trim();
}

/**
 * Авто-подбор иконки по названию (и, для оборудования, категории).
 * Никогда не возвращает null — при отсутствии совпадений отдаёт фолбэк
 * по типу сущности.
 */
export function resolveIconName(
  kind: IconEntityKind,
  name: string | null | undefined,
  category?: string | null,
): IconName {
  const text = normalize(name ?? '');

  if (kind === 'room') {
    return matchRules(text, ROOM_RULES) ?? FALLBACK_BY_KIND.room;
  }

  if (kind === 'equipment') {
    // Категория — более надёжный сигнал, чем название; проверяем её первой.
    if (category != null) {
      const byCat = EQUIPMENT_CATEGORY_ICON[category.toLowerCase()];
      if (byCat !== undefined) return byCat;
    }
    return (
      matchRules(text, EQUIPMENT_RULES) ??
      matchRules(text, ITEM_RULES) ??
      FALLBACK_BY_KIND.equipment
    );
  }

  if (kind === 'property') {
    // Иконка собственности подбирается по названию (aidepo-2): «дача…» →
    // dacha, «гараж…» → garageBuilding и т. д. Тип как выбор убран.
    return matchRules(text, PROPERTY_RULES) ?? FALLBACK_BY_KIND.property;
  }

  // kind === 'item'
  return matchRules(text, ITEM_RULES) ?? FALLBACK_BY_KIND.item;
}

/**
 * Единый хелпер отрисовки: «дай иконку сущности».
 *  - если `icon` задан вручную и валиден → используем его;
 *  - иначе → авто-подбор по названию / категории.
 */
export function entityIcon(
  kind: IconEntityKind,
  opts: {
    readonly icon?: string | null | undefined;
    readonly name?: string | null | undefined;
    readonly category?: string | null | undefined;
  },
): IconName {
  if (isIconName(opts.icon)) return opts.icon;
  return resolveIconName(kind, opts.name, opts.category);
}
